import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';

import { AdminShell } from '../../components/admin-shell/AdminShell';
import { adminGet } from './_api';

type CohortVenueLatest = {
  id: string;
  name: string;
  slug: string;
  city: string;
  country: string;
  position: number | null;
  published_at: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
};

type CohortVenueEarliest = {
  id: string;
  name: string;
  slug: string;
  position: number | null;
  published_at: string | null;
};

type Cohort = {
  cap: number;
  claimed: number;
  remaining: number;
  latest: CohortVenueLatest[];
  earliest: CohortVenueEarliest[];
};

export const Route = createFileRoute('/admin/cohort')({
  component: AdminCohort,
});

function AdminCohort() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'cohort'],
    queryFn: () => adminGet<Cohort>('/api/admin/cohort'),
  });

  if (isLoading) {
    return (
      <AdminShell>
        <Stack gap="md" py="lg" align="center">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading cohort data…
          </Text>
        </Stack>
      </AdminShell>
    );
  }

  if (isError || !data) {
    return (
      <AdminShell>
        <Stack gap="md">
          <Title order={2}>Founding cohort</Title>
          <Alert color="red" variant="light" title="Couldn’t load cohort data">
            <Stack gap="xs">
              <Text size="sm">
                {error instanceof Error
                  ? error.message
                  : 'The /api/admin/cohort endpoint did not respond. Refresh and try again.'}
              </Text>
              <Group>
                <Button size="xs" variant="default" onClick={() => void refetch()}>
                  Retry
                </Button>
              </Group>
            </Stack>
          </Alert>
        </Stack>
      </AdminShell>
    );
  }

  // Defensive normalisation — the API contract guarantees these shapes, but
  // shielding the render path against an unexpected response is what keeps
  // /admin/cohort from going blank if the worker ever drifts.
  const cap = typeof data.cap === 'number' && data.cap > 0 ? data.cap : 50;
  const claimed = typeof data.claimed === 'number' ? data.claimed : 0;
  const remaining =
    typeof data.remaining === 'number' ? data.remaining : Math.max(0, cap - claimed);
  const latest: CohortVenueLatest[] = Array.isArray(data.latest) ? data.latest : [];
  const earliest: CohortVenueEarliest[] = Array.isArray(data.earliest) ? data.earliest : [];
  const pct = Math.min(100, Math.max(0, Math.round((claimed / cap) * 100)));

  return (
    <AdminShell>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
          <div>
            <Title order={2}>Founding cohort</Title>
            <Text c="dimmed" size="sm">
              The first {cap} verified venues to publish are grandfathered onto the
              founding rate for the life of their account.
            </Text>
          </div>
          <Tooltip
            label="Cohort assignment is currently automatic. Venues join when they're verified and published. Manual assignment ships with the admin venues panel."
            withArrow
            multiline
            w={280}
          >
            <Button variant="default" disabled>
              Add venue to cohort (coming soon)
            </Button>
          </Tooltip>
        </Group>

        <Card withBorder padding="lg">
          <Stack gap="sm">
            <Group justify="space-between" align="baseline" wrap="wrap">
              <Title order={3}>
                {claimed} of {cap} claimed
              </Title>
              <Group gap="xs">
                <Badge variant="light" color={remaining === 0 ? 'tide' : 'sun'}>
                  {remaining} remaining
                </Badge>
                <Text c="dimmed" size="sm">
                  {pct}% full
                </Text>
              </Group>
            </Group>
            <Progress value={pct} aria-label={`${claimed} of ${cap} cohort positions claimed`} />
          </Stack>
        </Card>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <Card withBorder padding="md">
            <Title order={5} mb="xs">
              Most recent claims
            </Title>
            {latest.length === 0 ? (
              <Text c="dimmed" size="sm">
                No founding venues yet. Positions will fill here as new venues are
                verified and published.
              </Text>
            ) : (
              <Table.ScrollContainer minWidth={420}>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Pos</Table.Th>
                      <Table.Th>Venue</Table.Th>
                      <Table.Th>Where</Table.Th>
                      <Table.Th>Owner</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {latest.map((v) => (
                      <Table.Tr key={v.id}>
                        <Table.Td>{v.position ?? '–'}</Table.Td>
                        <Table.Td>
                          <Link
                            to="/venues/$slug"
                            params={{ slug: v.slug }}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            <Text size="sm" fw={500}>
                              {v.name}
                            </Text>
                          </Link>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed">
                            {[v.city, v.country].filter(Boolean).join(', ') || '–'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed">
                            {v.owner_name ?? '–'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Card>

          <Card withBorder padding="md">
            <Title order={5} mb="xs">
              Earliest claims
            </Title>
            {earliest.length === 0 ? (
              <Text c="dimmed" size="sm">
                No founding venues yet.
              </Text>
            ) : (
              <Table.ScrollContainer minWidth={360}>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Pos</Table.Th>
                      <Table.Th>Venue</Table.Th>
                      <Table.Th>Joined</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {earliest.map((v) => (
                      <Table.Tr key={v.id}>
                        <Table.Td>{v.position ?? '–'}</Table.Td>
                        <Table.Td>
                          <Link
                            to="/venues/$slug"
                            params={{ slug: v.slug }}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            <Text size="sm" fw={500}>
                              {v.name}
                            </Text>
                          </Link>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed">
                            {v.published_at
                              ? new Date(v.published_at).toLocaleDateString(undefined, {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })
                              : '–'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Card>
        </SimpleGrid>
      </Stack>
    </AdminShell>
  );
}
